import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import styled, { keyframes, css } from "styled-components";
import { publicRequest } from "../requestMethods";
import { getSocket } from "../socket";
import theme from "../theme";
import { IconMessage, IconPaperclip, IconSearch, IconX, IconDocument } from "../icons";
import { playTing } from "../tingSound";
import OnlineDot from "./OnlineDot";
import { useOnlineStatus } from "../hooks/useOnlineStatus";


const popUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(192,122,16,0.4); }
  50%       { box-shadow: 0 0 0 8px rgba(192,122,16,0); }
`;


const FloatBtn = styled.button`
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  border: none;
  background: ${theme.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(192,122,16,0.35);
  transition: transform 0.2s, box-shadow 0.2s;
  z-index: 900;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(192,122,16,0.45);
  }

  ${(p) => p.$pulse && css`animation: ${pulse} 2s ease-in-out infinite;`}
`;

const FloatBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  background: ${theme.danger};
  color: #fff;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  padding: 0 3px;
`;


const Panel = styled.div`
  position: fixed;
  bottom: 90px;
  right: 28px;
  width: 340px;
  height: 480px;
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 16px;
  box-shadow: 0 16px 60px rgba(26,18,8,0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 899;
  animation: ${popUp} 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  border-bottom: 1.5px solid ${theme.separator};
  flex-shrink: 0;
  background: ${theme.bg};
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.textMain};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackBtn = styled.button`
  background: transparent;
  border: none;
  color: ${theme.textMuted};
  cursor: pointer;
  font-size: 18px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  transition: color 0.15s;
  &:hover { color: ${theme.textMain}; }
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: ${theme.textMuted};
  cursor: pointer;
  font-size: 20px;
  padding: 0 2px;
  display: flex;
  align-items: center;
  line-height: 1;
  transition: color 0.15s;
  &:hover { color: ${theme.danger}; }
`;


const UserList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const UserRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 16px;
  background: ${(p) => (p.$hasUnread ? theme.primaryLight : "transparent")};
  border: none;
  border-bottom: 1px solid ${theme.separator};
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;

  &:hover { background: ${theme.bgAlt}; }
  &:last-child { border-bottom: none; }
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${theme.bgAlt};
  border: 1.5px solid ${theme.cardBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: ${theme.primary};
  flex-shrink: 0;
  overflow: hidden;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.textMain};
  display: block;
`;

const UserSub = styled.span`
  font-size: 11px;
  color: ${theme.textMuted};
`;

const UnreadBubble = styled.span`
  background: ${theme.primary};
  color: #fff;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  flex-shrink: 0;
`;

const EmptyMsg = styled.p`
  font-size: 13px;
  color: ${theme.textMuted};
  text-align: center;
  padding: 40px 20px;
  line-height: 1.5;
`;


const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MsgBubble = styled.div`
  max-width: 80%;
  padding: ${(p) => (p.$mine ? "8px 12px 6px 12px" : "8px 12px")};
  border-radius: ${(p) => (p.$mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px")};
  background: ${(p) => (p.$mine ? theme.primary : theme.bgAlt)};
  color: ${(p) => (p.$mine ? "#fff" : theme.textMain)};
  font-size: 13px;
  line-height: 1.45;
  align-self: ${(p) => (p.$mine ? "flex-end" : "flex-start")};
  word-break: break-word;
  animation: ${slideIn} 0.18s ease both;
  position: relative;
`;

/* time + tick row inside bubble */
const MsgMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  margin-top: 3px;
`;

const MsgTime = styled.span`
  font-size: 10px;
  color: ${(p) => (p.$mine ? "rgba(255,255,255,0.65)" : theme.textMuted)};
  line-height: 1;
`;

/* Double-tick SVG — single tick = only first check visible */
function TickIcon({ read }) {
  return (
    <svg width="32" height="22" viewBox="0 -0.5 25 25" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      {/* first check (left) */}
      <path
        d="M3.96967 12.5303L7.96967 16.5303L9.03033 15.4697L5.03033 11.4697L3.96967 12.5303ZM9.03033 16.5303L17.0303 8.53033L15.9697 7.46967L7.96967 15.4697L9.03033 16.5303Z"
        fill={read ? "#60a5fa" : "rgba(255,255,255,0.55)"}
      />
      {/* second check (right) — only shown for double tick */}
      <path
        d="M7.96967 12.5303L11.9697 16.5303L13.0303 15.4697L9.03033 11.4697L7.96967 12.5303ZM13.0303 16.5303L21.0303 8.53033L19.9697 7.46967L11.9697 15.4697L13.0303 16.5303Z"
        fill={read ? "#60a5fa" : "rgba(255,255,255,0.55)"}
      />
    </svg>
  );
}

const MsgInput = styled.input`
  flex: 1;
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-family: inherit;
  color: ${theme.textMain};
  outline: none;
  transition: border-color 0.2s;

  &::placeholder { color: ${theme.textMuted}; }
  &:focus { border-color: ${theme.primary}; }
`;

const SendBtn = styled.button`
  padding: 8px 14px;
  background: ${theme.primary};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  flex-shrink: 0;

  &:hover { background: ${theme.primaryDark}; }
  &:active { transform: scale(0.96); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ── Typing indicator ── */
const typingDot = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40%            { transform: scale(1);   opacity: 1; }
`;

const TypingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 2px 2px;
  align-self: flex-start;
`;

const TypingDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${theme.textMuted};
  display: inline-block;
  animation: ${typingDot} 1.8s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || "0s"};
`;

const TypingLabel = styled.span`
  font-size: 11px;
  color: ${theme.textMuted};
  margin-left: 4px;
`;

const ConversationTools = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px 0;
`;

const SearchWrap = styled.div`
    flex: 1;
    position: relative;
`;

const SearchInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    background: ${theme.bg};
    border: 1.5px solid ${theme.cardBorder};
    border-radius: 10px;
    padding: 8px 10px 8px 32px;
    font-size: 12px;
    font-family: inherit;
    color: ${theme.textMain};
    outline: none;

    &::placeholder { color: ${theme.textMuted}; }
    &:focus { border-color: ${theme.primary}; }
`;

const SearchIconWrap = styled.span`
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${theme.textMuted};
    display: flex;
    align-items: center;
`;

const SearchHint = styled.div`
    padding: 4px 12px 0;
    font-size: 10.5px;
    color: ${theme.textMuted};
`;

const ComposeMeta = styled.div`
    padding: 8px 12px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const MentionSuggestList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 2px;
`;

const MentionSuggestItem = styled.button`
    border: 1px solid ${theme.cardBorder};
    background: ${theme.cardBg};
    color: ${theme.textMain};
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 700;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;

    &:hover {
        border-color: ${theme.primary};
        background: ${theme.primaryLight};
    }
`;

const MentionHint = styled.span`
    font-size: 10.5px;
    font-weight: 600;
    color: ${theme.textMuted};
`;

const DraftRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const NotePill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: ${theme.primaryLight};
    color: ${theme.primary};
    border-radius: 999px;
    padding: 5px 9px;
    font-size: 11px;
    font-weight: 700;
`;

const AttachmentCard = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${theme.bgAlt};
    border: 1px solid ${theme.separator};
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 11.5px;
    color: ${theme.textSub};
`;

const AttachmentThumb = styled.img`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
`;

const MessageStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const MessageNoteRefs = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const MessageNoteRef = styled.button`
    background: ${theme.bgAlt};
    border: 1px solid ${theme.cardBorder};
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    color: ${theme.textSub};
    cursor: pointer;

    &:hover {
        border-color: ${theme.primary};
        color: ${theme.primary};
    }
`;

const ComposeRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-top: 1.5px solid ${theme.separator};
    flex-shrink: 0;
    background: ${theme.bg};
`;

const AttachBtn = styled.button`
    width: 34px;
    height: 34px;
    border-radius: 10px;
    border: 1.5px solid ${theme.cardBorder};
    background: ${theme.cardBg};
    color: ${theme.textMuted};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover { color: ${theme.primary}; border-color: ${theme.primary}; }
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const FileRemoveBtn = styled.button`
    background: transparent;
    border: none;
    color: inherit;
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 0;
`;

const EmptySearch = styled.p`
    font-size: 13px;
    color: ${theme.textMuted};
    text-align: center;
    padding: 40px 20px;
    line-height: 1.5;
`;

/* ── Online status label in header ── */
const OnlineLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: ${(p) => (p.$online ? "#22c55e" : theme.textMuted)};
  margin-left: 4px;
`;

/* ── Avatar wrapper (needs position:relative for dot) ── */
const AvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
`;


function formatTime(d) {
    const date = new Date(d);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const STATIC_URL = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api").replace("/api", "");

function UserAvatar({ user }) {
    if (user?.profilePicture) {
        return <AvatarImg src={`${STATIC_URL}/images/${user.profilePicture}`} alt={user.username} />;
    }
    return <>{user?.username?.[0]?.toUpperCase() || "?"}</>;
}


function IconArrowLeft() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );
}

function normalizeNoteKey(value) {
    return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
}

function findMentionedNotes(text, noteCatalog) {
    const tokens = [...String(text || "").matchAll(/@([a-z0-9_]+)/gi)].map((match) => match[1]);
    if (tokens.length === 0) return [];

    const lookup = new Map();
    noteCatalog.forEach((note) => {
        lookup.set(normalizeNoteKey(note.notename), note);
    });

    const refs = [];
    const seen = new Set();
    tokens.forEach((token) => {
        const note = lookup.get(normalizeNoteKey(token));
        if (note && !seen.has(note._id)) {
            seen.add(note._id);
            refs.push({ noteId: note._id, noteName: note.notename });
        }
    });

    return refs;
}

function extractActiveMentionQuery(text) {
    const match = String(text || "").match(/(?:^|\s)@([a-z0-9_]*)$/i);
    return match ? match[1] : null;
}

function mergeNoteRefs(a, b) {
    const out = [];
    const seen = new Set();
    [...(a || []), ...(b || [])].forEach((ref) => {
        if (!ref?.noteId || seen.has(ref.noteId)) return;
        seen.add(ref.noteId);
        out.push({ noteId: ref.noteId, noteName: ref.noteName });
    });
    return out;
}

function stripMentionTokens(text, noteRefs) {
    let output = String(text || "");
    (noteRefs || []).forEach((ref) => {
        const token = normalizeNoteKey(ref.noteName);
        if (!token) return;
        const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        output = output.replace(new RegExp(`(^|\\s)@${escaped}(?=\\s|$)`, "ig"), " ");
    });
    return output.replace(/\s+/g, " ").trim();
}


export default function ChatWidget() {
    const currentUser = useSelector((s) => s.user.currentUser);
    const [open, setOpen] = useState(false);
    const [following, setFollowing] = useState([]);
    const [activeUser, setActiveUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [msgText, setMsgText] = useState("");
    const [unreadCounts, setUnreadCounts] = useState({});
    const [sending, setSending] = useState(false);
    const [peerTyping, setPeerTyping] = useState(false);
    const [conversationQuery, setConversationQuery] = useState("");
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const [noteCatalog, setNoteCatalog] = useState([]);
    const [selectedNoteRefs, setSelectedNoteRefs] = useState([]);
    const typingTimeoutRef = useRef(null);
    const typingEmitRef = useRef(false);
    const bottomRef = useRef(null);
    const panelRef = useRef(null);
    const fileInputRef = useRef(null);

    const followingIds = following.map((u) => u._id);
    const onlineSet = useOnlineStatus(currentUser?._id, followingIds);

    const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);


    const fetchFollowing = useCallback(async () => {
        if (!currentUser) return;
        try {
            const me = await publicRequest.get(`/users/${currentUser._id}`);
            const ids = me.data.followings || [];
            const users = await Promise.all(ids.map((id) => publicRequest.get(`/users/${id}`).then((r) => r.data).catch(() => null)));
            setFollowing(users.filter(Boolean));
        } catch { }
    }, [currentUser]);


    const fetchUnread = useCallback(async () => {
        if (!currentUser) return;
        try {
            const res = await publicRequest.get(`/messages/${currentUser._id}/unread-counts`);
            setUnreadCounts(res.data || {});
        } catch { }
    }, [currentUser]);

    useEffect(() => {
        fetchFollowing();
        fetchUnread();
    }, [fetchFollowing, fetchUnread]);

    useEffect(() => {
        if (!currentUser) return;
        publicRequest.get(`/notes/profile/${currentUser._id}`)
            .then((res) => setNoteCatalog(res.data || []))
            .catch(() => setNoteCatalog([]));
    }, [currentUser]);


    useEffect(() => {
        if (!currentUser) return;
        const socket = getSocket(currentUser._id);

        const handleMsg = (msg) => {

            const isActiveConversation = activeUser
                && ((msg.sender === currentUser._id && msg.receiver === activeUser._id)
                    || (msg.sender === activeUser._id && msg.receiver === currentUser._id));

            if (isActiveConversation) {
                setMessages((prev) => {
                    const alreadyHas = prev.some((m) => m._id === msg._id);
                    if (alreadyHas) return prev;
                    return [...prev, msg];
                });
            }


            if (msg.sender !== currentUser._id) {
                playTing();
                setActiveUser((cur) => {
                    if (cur && cur._id === msg.sender) {
                        // mark as read immediately and notify sender via socket
                        publicRequest.put(`/messages/${currentUser._id}/${msg.sender}/read`).then(() => {
                            const socket = getSocket(currentUser._id);
                            socket.emit("messages_read", { readerId: currentUser._id, senderId: msg.sender });
                        }).catch(() => { });
                        return cur;
                    }

                    setUnreadCounts((prev) => ({
                        ...prev,
                        [msg.sender]: (prev[msg.sender] || 0) + 1,
                    }));
                    return cur;
                });
            }
        };

        socket.on("new_message", handleMsg);
        return () => socket.off("new_message", handleMsg);
    }, [currentUser, activeUser]);

    // Listen for read receipts from the other side
    useEffect(() => {
        if (!currentUser) return;
        const socket = getSocket(currentUser._id);
        const handleRead = ({ readerId }) => {
            setActiveUser((cur) => {
                if (cur && cur._id === readerId) {
                    setMessages((prev) => prev.map((m) =>
                        m.sender === currentUser._id ? { ...m, read: true } : m
                    ));
                }
                return cur;
            });
        };
        socket.on("messages_read", handleRead);
        return () => socket.off("messages_read", handleRead);
    }, [currentUser]);

    // Typing listeners
    useEffect(() => {
        if (!currentUser) return;
        const socket = getSocket(currentUser._id);

        const handleTypingStart = ({ senderId }) => {
            setActiveUser((cur) => {
                if (cur && cur._id === senderId) setPeerTyping(true);
                return cur;
            });
        };
        const handleTypingStop = ({ senderId }) => {
            setActiveUser((cur) => {
                if (cur && cur._id === senderId) setPeerTyping(false);
                return cur;
            });
        };

        socket.on("typing_start", handleTypingStart);
        socket.on("typing_stop", handleTypingStop);
        return () => {
            socket.off("typing_start", handleTypingStart);
            socket.off("typing_stop", handleTypingStop);
        };
    }, [currentUser]);


    useEffect(() => {
        if (!currentUser || !activeUser) return;
        publicRequest.get(`/messages/${currentUser._id}/${activeUser._id}`)
            .then((r) => setMessages(r.data))
            .catch(() => setMessages([]));


        publicRequest.put(`/messages/${currentUser._id}/${activeUser._id}/read`).catch(() => { });
        setUnreadCounts((prev) => {
            const next = { ...prev };
            delete next[activeUser._id];
            return next;
        });
    }, [activeUser, currentUser]);


    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        if (activeUser) {
            setConversationQuery("");
            setSelectedAttachment(null);
        }
    }, [activeUser]);


    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const handleOpenUser = (user) => {
        setActiveUser(user);
        setMessages([]);
        setPeerTyping(false);
        setConversationQuery("");
        setSelectedAttachment(null);
    };

    const handleBack = () => {
        // stop typing if we were typing
        if (activeUser && typingEmitRef.current) {
            const socket = getSocket(currentUser._id);
            socket.emit("typing_stop", { senderId: currentUser._id, receiverId: activeUser._id });
            typingEmitRef.current = false;
        }
        setActiveUser(null);
        setMessages([]);
        setPeerTyping(false);
        setConversationQuery("");
        setSelectedAttachment(null);
    };

    const handleInputChange = (e) => {
        setMsgText(e.target.value);
        if (!activeUser || !currentUser) return;
        const socket = getSocket(currentUser._id);
        if (!typingEmitRef.current) {
            typingEmitRef.current = true;
            socket.emit("typing_start", { senderId: currentUser._id, receiverId: activeUser._id });
        }
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            typingEmitRef.current = false;
            socket.emit("typing_stop", { senderId: currentUser._id, receiverId: activeUser._id });
        }, 1500);
    };

    const handleSelectMention = (note) => {
        const mentionToken = `@${normalizeNoteKey(note.notename)}`;
        setMsgText((prev) => prev.replace(/(?:^|\s)@[a-z0-9_]*$/i, (m) => `${m[0] === " " ? " " : ""}${mentionToken} `));
        setSelectedNoteRefs((prev) => mergeNoteRefs(prev, [{ noteId: note._id, noteName: note.notename }]));
    };

    const handleOpenNoteRef = async (ref) => {
        if (!ref?.noteId) return;
        try {
            const res = await publicRequest.get(`/notes/${ref.noteId}`);
            const note = res.data;
            if (note?.notefilename) {
                window.open(`${STATIC_URL}/images/${note.notefilename}`, "_blank", "noopener,noreferrer");
                return;
            }

            window.open(`${process.env.REACT_APP_BASE_URL}/notes/${ref.noteId}/content`, "_blank", "noopener,noreferrer");
        } catch {
            window.open(`${process.env.REACT_APP_BASE_URL}/notes/${ref.noteId}/content`, "_blank", "noopener,noreferrer");
        }
    };

    const handleAttachmentPick = (e) => {
        const file = e.target.files?.[0] || null;
        setSelectedAttachment(file);
        e.target.value = "";
    };

    const handleSend = (e) => {
        e.preventDefault();
        const noteRefs = mergeNoteRefs(selectedNoteRefs, findMentionedNotes(msgText, noteCatalog));
        const cleanedText = stripMentionTokens(msgText, noteRefs);
        const hasPayload = Boolean(cleanedText || selectedAttachment || noteRefs.length > 0);
        if (!activeUser || sending || !hasPayload) return;
        setSending(true);
        // stop typing indicator
        clearTimeout(typingTimeoutRef.current);
        if (typingEmitRef.current) {
            const socket = getSocket(currentUser._id);
            socket.emit("typing_stop", { senderId: currentUser._id, receiverId: activeUser._id });
            typingEmitRef.current = false;
        }

        const formData = new FormData();
        formData.append("text", cleanedText);
        if (selectedAttachment) formData.append("attachment", selectedAttachment);
        formData.append("noteRefs", JSON.stringify(noteRefs));

        publicRequest.post(`/messages/${currentUser._id}/${activeUser._id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        })
            .then((res) => {
                setMessages((prev) => (prev.some((message) => message._id === res.data._id) ? prev : [...prev, res.data]));
                setMsgText("");
                setSelectedAttachment(null);
                setSelectedNoteRefs([]);
            })
            .finally(() => setSending(false));
    };

    const visibleMessages = messages.filter((message) => {
        const query = conversationQuery.trim().toLowerCase();
        if (!query) return true;

        const haystack = [
            message.text,
            message.noteRefs?.map((ref) => ref.noteName).join(" "),
            message.attachment?.name,
            message.sender === currentUser?._id ? currentUser?.username : activeUser?.username,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        return haystack.includes(query);
    });

    const activeMentionQuery = extractActiveMentionQuery(msgText);
    const mentionSuggestions = (() => {
        if (activeMentionQuery == null) return [];
        const q = normalizeNoteKey(activeMentionQuery);
        const taken = new Set((selectedNoteRefs || []).map((ref) => ref.noteId));
        return noteCatalog
            .filter((note) => !taken.has(note._id))
            .filter((note) => {
                const key = normalizeNoteKey(note.notename);
                return q === "" || key.includes(q);
            })
            .slice(0, 5);
    })();

    const draftNoteRefs = mergeNoteRefs(selectedNoteRefs, findMentionedNotes(msgText, noteCatalog));

    if (!currentUser) return null;

    return (
        <>
            {}
            <FloatBtn
                onClick={() => setOpen((v) => !v)}
                $pulse={totalUnread > 0}
                aria-label={`Chat${totalUnread > 0 ? ` (${totalUnread} unread)` : ""}`}
                id="chat-widget-btn"
            >
                <IconMessage size="20px" />
                {totalUnread > 0 && <FloatBadge>{totalUnread > 9 ? "9+" : totalUnread}</FloatBadge>}
            </FloatBtn>

            {}
            {open && (
                <Panel ref={panelRef} id="chat-panel">
                    <PanelHeader>
                        <HeaderTitle>
                            {activeUser ? (
                                <>
                                    <BackBtn onClick={handleBack} aria-label="Back to conversations">
                                        <IconArrowLeft />
                                    </BackBtn>
                                    <AvatarWrap style={{ width: 24, height: 24 }}>
                                        <Avatar style={{ width: 24, height: 24, fontSize: 11 }}>
                                            <UserAvatar user={activeUser} />
                                        </Avatar>
                                        <OnlineDot online={onlineSet.has(activeUser._id)} size="8px" borderColor={theme.bg} />
                                    </AvatarWrap>
                                    {activeUser.username}
                                    <OnlineLabel $online={onlineSet.has(activeUser._id)}>
                                        {onlineSet.has(activeUser._id) ? "● Online" : "● Offline"}
                                    </OnlineLabel>
                                </>
                            ) : (
                                <><IconMessage size="13px" /> Messages</>
                            )}
                        </HeaderTitle>
                        <CloseBtn onClick={() => setOpen(false)} aria-label="Close chat">×</CloseBtn>
                    </PanelHeader>

                    {activeUser && (
                        <>
                            <ConversationTools>
                                <SearchWrap>
                                    <SearchIconWrap>
                                        <IconSearch size="12px" />
                                    </SearchIconWrap>
                                    <SearchInput
                                        value={conversationQuery}
                                        onChange={(e) => setConversationQuery(e.target.value)}
                                        placeholder="Search messages or note names"
                                        aria-label="Search conversation"
                                    />
                                </SearchWrap>
                            </ConversationTools>
                            <SearchHint>
                                Type <strong>@</strong> then note name. Suggestions appear as you type and selected notes attach before sending.
                            </SearchHint>
                        </>
                    )}

                    {}
                    {!activeUser && (
                        <UserList>
                            {following.length === 0 ? (
                                <EmptyMsg>Follow some users to start chatting!</EmptyMsg>
                            ) : (
                                following.map((u) => {
                                    const unread = unreadCounts[u._id] || 0;
                                    const isOnline = onlineSet.has(u._id);
                                    return (
                                        <UserRow key={u._id} onClick={() => handleOpenUser(u)} $hasUnread={unread > 0}>
                                            <AvatarWrap>
                                                <Avatar>
                                                    <UserAvatar user={u} />
                                                </Avatar>
                                                <OnlineDot online={isOnline} size="9px" borderColor={theme.cardBg} />
                                            </AvatarWrap>
                                            <UserInfo>
                                                <UserName>{u.username}</UserName>
                                                <UserSub>
                                                    {isOnline ? <span style={{ color: "#22c55e", fontWeight: 600 }}>Online</span> : (u.institution || "NotesHub user")}
                                                </UserSub>
                                            </UserInfo>
                                            {unread > 0 && <UnreadBubble>{unread > 9 ? "9+" : unread}</UnreadBubble>}
                                        </UserRow>
                                    );
                                })
                            )}
                        </UserList>
                    )}

                    {}
                    {activeUser && (
                        <>
                            <MessageList>
                                {visibleMessages.length === 0 && (
                                    <EmptySearch style={{ paddingTop: 60 }}>
                                        {conversationQuery.trim() ? "No matching messages." : "No messages yet. Say hello!"}
                                    </EmptySearch>
                                )}
                                {visibleMessages.map((m) => {
                                    const mine = m.sender === currentUser._id;
                                    const hasAttachment = Boolean(m?.attachment?.url && m?.attachment?.name);
                                    return (
                                        <MessageStack key={m._id}>
                                            <MsgBubble $mine={mine}>
                                                {m.text ? <span>{m.text}</span> : null}
                                                {m.noteRefs?.length > 0 && (
                                                    <MessageNoteRefs>
                                                        {m.noteRefs.map((ref) => (
                                                            <MessageNoteRef
                                                                key={ref.noteId}
                                                                type="button"
                                                                title={`Open ${ref.noteName}`}
                                                                onClick={() => handleOpenNoteRef(ref)}
                                                            >
                                                                <IconDocument size="10px" />
                                                                {ref.noteName}
                                                            </MessageNoteRef>
                                                        ))}
                                                    </MessageNoteRefs>
                                                )}
                                                {hasAttachment && (
                                                    <AttachmentCard>
                                                        {m.attachment.kind === "image" ? (
                                                            <AttachmentThumb src={`${STATIC_URL}${m.attachment.url}`} alt={m.attachment.name} />
                                                        ) : (
                                                            <IconPaperclip size="13px" />
                                                        )}
                                                        <span>{m.attachment.name}</span>
                                                    </AttachmentCard>
                                                )}
                                                {mine && (
                                                    <MsgMeta>
                                                        <MsgTime $mine>{formatTime(m.createdAt)}</MsgTime>
                                                        <TickIcon read={m.read} />
                                                    </MsgMeta>
                                                )}
                                                {!mine && (
                                                    <MsgMeta style={{ justifyContent: "flex-start" }}>
                                                        <MsgTime $mine={false}>{formatTime(m.createdAt)}</MsgTime>
                                                    </MsgMeta>
                                                )}
                                            </MsgBubble>
                                        </MessageStack>
                                    );
                                })}
                                {peerTyping && (
                                    <TypingRow>
                                        <TypingDot $delay="0s" />
                                        <TypingDot $delay="0.2s" />
                                        <TypingDot $delay="0.4s" />
                                        <TypingLabel>{activeUser.username} is typing…</TypingLabel>
                                    </TypingRow>
                                )}
                                <div ref={bottomRef} />
                            </MessageList>

                            <ComposeMeta>
                                {activeUser && mentionSuggestions.length > 0 && (
                                    <MentionSuggestList>
                                        {mentionSuggestions.map((note) => (
                                            <MentionSuggestItem key={note._id} type="button" onClick={() => handleSelectMention(note)}>
                                                <IconDocument size="12px" />
                                                {note.notename}
                                                <MentionHint>@{normalizeNoteKey(note.notename)}</MentionHint>
                                            </MentionSuggestItem>
                                        ))}
                                    </MentionSuggestList>
                                )}
                                {draftNoteRefs.length > 0 && (
                                    <DraftRow>
                                        {draftNoteRefs.map((ref) => (
                                            <NotePill key={ref.noteId}>
                                                <IconDocument size="10px" />
                                                {ref.noteName}
                                            </NotePill>
                                        ))}
                                    </DraftRow>
                                )}
                                {selectedAttachment && (
                                    <AttachmentCard>
                                        <IconPaperclip size="13px" />
                                        <span>{selectedAttachment.name}</span>
                                        <FileRemoveBtn type="button" onClick={() => setSelectedAttachment(null)} aria-label="Remove attachment">
                                            <IconX size="11px" />
                                        </FileRemoveBtn>
                                    </AttachmentCard>
                                )}
                            </ComposeMeta>

                            <ComposeRow onSubmit={handleSend} as="form">
                                <HiddenFileInput ref={fileInputRef} type="file" onChange={handleAttachmentPick} />
                                <AttachBtn type="button" onClick={() => fileInputRef.current?.click()} aria-label="Attach a file">
                                    <IconPaperclip size="14px" />
                                </AttachBtn>
                                <MsgInput
                                    value={msgText}
                                    onChange={handleInputChange}
                                    placeholder={`Message ${activeUser.username}…`}
                                    autoFocus
                                    id="chat-msg-input"
                                    maxLength={2000}
                                />
                                <SendBtn type="submit" disabled={sending || (!msgText.trim() && !selectedAttachment && draftNoteRefs.length === 0)} id="chat-send-btn">
                                    Send
                                </SendBtn>
                            </ComposeRow>
                        </>
                    )}
                </Panel>
            )}
        </>
    );
}
